/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable complexity,@typescript-eslint/no-unused-vars */
import { HighwayIcon } from '@assets';
import {
  Box,
  Button,
  Checkbox,
  CollapsibleCard,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { OpenInNewIcon, TruckIcon } from '@atgfr8/icons';
import {
  checkZipForChanges,
  hasQualifiedStatus,
} from '@common/order/helperFunctions';
import {
  BasicModal,
  CardPlaceholder,
  FlagPopup,
  Notification,
  ToastType,
} from '@components';
import { flagImgs, softDeleteFlagTypes } from '@components/FlagPopup/constants';
import { SpecialFlagTypeEnum } from '@components/FlagPopup/types';
import { SelectInputFilled } from '@components/Select/SelectInputFilled';
import { FeatureFlags } from '@constants/featureFlags';
import { carrierUrls } from '@constants/urls';
import { A, useParams, useSearchParams } from '@solidjs/router';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { LTLLoadItem, LTLTrackingData } from '@store/ltl/types';
import { closeModal, openModal } from '@store/modals';
import {
  addCommentInOrderStore,
  applyOrderHydration,
  carrierStore,
  dispatchLtlLoad,
  fetchLtlQuoteLoadItems,
  fetchLtlQuoteParametersByLoadId,
  fetchNearbyCarriers,
  fetchUserInfoUserModel,
  isCreditLimitExceeded,
  orderContainsLTLLoad,
  orderStore,
  postOrderComment,
  requestPickupLegacy,
  saveOrderDetails,
  setActiveCarrierTab,
  setOrderStore,
  updateCarrierStoreContactsWithLoadCarrier,
  updateLoadPropertyAtIndex,
  updateOrderState,
  updateVendorPropertyAtIndex,
} from '@store/orders';
import {
  CommitQuoteResult,
  FlagType,
  LtlQuoteLoadItemViewModel,
  LtlQuoteParametersViewModel,
  TabState,
  UserInfoModel,
} from '@store/orders/types';
import { userStore } from '@store/user';
import { FlagViewModel } from '@typeDefinitions/flagTypes';
import { VendorLineItem } from '@typeDefinitions/lineItemTypes';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import {
  getEligibleEntries,
  handleToast,
  isOrderStoreDirty,
} from '@utils/utils';
import { getLoadColor } from '@views/order/components/header/constants';
import { AssignLoad, SelectPlaylist } from '@views/order/components/models';
import {
  ADD_PLAYLIST_ID,
  ASSIGN_LOAD_ID,
  getLoadStatusItemsForMode,
  NEARBY_CARRIER_MODAL_ID,
} from '@views/order/constants';
import { size } from 'lodash';
import { DateTime } from 'luxon';
import {
  Accessor,
  createEffect,
  createSignal,
  Index,
  Match,
  onMount,
  Show,
  Switch,
} from 'solid-js';
import { unwrap } from 'solid-js/store';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { markOrderLoadFieldChangeSource } from '@changeTracking/pages/orderDetails/formChangeTracking';
import ShareAndUnShareCapacity from '@views/order/components/shared/ShareAndUnShareCapacity';

import { SystemAlerts } from '../../systemAlerts';
import { GetRates } from '../GetRates';
import LoadInformationActionButtons from '../LoadInformationActionButtons';
import { LTLInfo } from '../LTLInfo';
import { NearbyCarrier } from '../NearbyCarrier';
import { AssignedTo } from './AssignedTo';
import { CapacityOpsRep } from './CapacityOpsRep';
import { CarrierAutoComplete } from './CarrierAutocomplete';
import { CarrierContact } from './CarrierContact';
import { CarrierDetailActionButtons } from './CarrierDetailsActions';
import { CoveredBy } from './CoveredBy';
import { DriverDetails } from './DriverDetails';
import {
  EscalatedFlagSubTextContent,
  EscalatedFlagTextContent,
} from './EscalatedFlagNotification';
import {
  FlagComments,
  FlagNotificationSubTextContent,
  FlagNotificationTextContent,
} from './FlagComponents';
import LaneHistory from './LaneHistory/LaneHistory';
import { LoadDetails } from './LoadDetails';
import { LoadDetailsRow } from './LoadDetailsRow';
import { LoadSureWarning } from './LoadSureWarning';
import { ProfileFallout } from './ProfileFallout';
import {
  RateConfirmationActivityHistory,
  rateConStatusBgAndTextColor,
} from './RateConfirmationActivityHistory';
import { loadInfoStore, setLoadInfoStore } from './store';
import { onFlagChange } from './utils';
import { isRequoteNeeded, ItemDifference } from './validateRequoteNeeded';

// Helper function to generate a detailed error message for requote
const getDetailedRequoteMessage = (differences: ItemDifference[]): string => {
  if (!differences.length) {
    return 'The load data has changed since the quote was created. Please requote before dispatching.';
  }

  const fieldNameMap: Record<string, string> = {
    itemCount: 'Item count',
    class: 'Freight class',
    weight: 'Weight',
    length: 'Length',
    width: 'Width',
    height: 'Height',
    quantity: 'Quantity',
    hazmat: 'Hazmat status',
    breakdownCount: 'Sub-item count',
    breakdownWeight: 'Weight',
    breakdownPieces: 'Pieces',
    breakdownClass: 'Freight class',
    breakdownHazmat: 'Hazmat status',
    originCountry: 'Origin country',
    originState: 'Origin state',
    originZip: 'Origin zip code',
    destinationCountry: 'Destination country',
    destinationState: 'Destination state',
    destinationZip: 'Destination zip code',
  };

  // Limit to the first 3 differences to avoid overly long messages
  const significantDifferences = differences.slice(0, 3);

  // Create a message with the key differences
  let message = 'Load data changes require requote: ';

  significantDifferences.forEach((diff, index) => {
    const fieldName = fieldNameMap[diff.fieldName] ?? diff.fieldName;
    if (index > 0) {
      message += ', ';
    }

    // Format the message based on if it's a load item with an index
    if (diff.index === undefined) {
      message += `${fieldName} (${diff.original} → ${diff.current})`;
    } else {
      const subItem =
        diff.subIndex === undefined ? '' : ` Sub-item ${diff.subIndex + 1}`;
      message += `Item ${diff.index + 1}${subItem} ${fieldName} (${diff.original} → ${diff.current})`;
    }
  });

  // If there are more differences, mention them
  if (differences.length > 3) {
    message += `, and ${differences.length - 3} more changes.`;
  }

  return message + ' Please requote before dispatching.';
};

export const LoadInfo = () => {
  const editFlagModalId = `EditFlagID-${orderStore.activeTab.index}`;
  const addPlaylistModalId = `${ADD_PLAYLIST_ID}-${orderStore.activeTab.index}`;
  const nearByCarriersModalId = `${NEARBY_CARRIER_MODAL_ID}-${orderStore.activeTab.index}`;

  const [isCarrierSelected, setIsCarrierSelected] = createSignal(false);
  const [pickupLoading, setPickupLoading] = createSignal(false);
  const [query] = useSearchParams();
  const [trackingData, setTrackingData] = createSignal<LTLTrackingData | null>(
    null,
  );
  const isCapacityFinderEnabled = isFeatureFlagEnabled(
    FeatureFlags.V4CapacityFinder,
  );
  const isLaneHistoryEnabled = isFeatureFlagEnabled(
    FeatureFlags.EnableLoadLaneHistory,
  );

  const [load, setLoad] = createSignal(
    orderStore.order.loads[orderStore.activeTab.index],
  );

  // Keep Profile visible but disabled unless the carrier ID produces a valid URL.
  const carrierProfileId = () => {
    const id = Number(load().carrierId);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
  };

  const [ltlQuoteParams, setLtlQuoteParams] =
    createSignal<LtlQuoteParametersViewModel>({
      id: 0,
      value: false,
      operationType: 'None',
      quoteDate: '',
      originZip: '',
      originCity: '',
      originState: '',
      originCountry: '',
      destinationZip: '',
      destinationCity: '',
      destinationState: '',
      destinationCountry: '',
      linearFeet: 0,
      isStackable: false,
      isLinearFeetOverridden: false,
      createdByName: '',
      provider: '',
      accessorials: [],
      ltlInsuranceQuote: null,

      quoteRequestLocator: '',
      willExpireSoon: false,
    });

  const [loadLTLItems, setLoadItems] = createSignal<
    LtlQuoteLoadItemViewModel[]
  >([]);

  const [isLtlQuoteParamsLoading, setIsLtlQuoteParamsLoading] =
    createSignal(false);

  const params = useParams();
  const isCreate = !params.id;

  createEffect(() => {
    if (Boolean(load().carrierId)) {
      setIsCarrierSelected(true);
    } else {
      setIsCarrierSelected(false);
    }
  });

  createEffect(() => {
    setLoad(orderStore.order.loads[orderStore.activeTab.index]);
  });

  createEffect(async () => {
    if (load().id) {
      setIsLtlQuoteParamsLoading(true);
      try {
        const quoteParam = await fetchLtlQuoteParametersByLoadId(load().id);

        if (!!quoteParam && quoteParam.id) {
          const loadItem = await fetchLtlQuoteLoadItems(quoteParam.id);
          setLoadItems(loadItem);
        }

        setLtlQuoteParams(quoteParam);
      } finally {
        setIsLtlQuoteParamsLoading(false);
      }
    }
  });

  onMount(() => {
    updateCarrierStoreContactsWithLoadCarrier(orderStore.activeTab.index);
  });

  createEffect(() => {
    void (async () => {
      if (isCreate) {
        const response: UserInfoModel = await fetchUserInfoUserModel(
          userStore.user.id,
        );
        setLoadInfoStore('coveredBy', response);
        setLoadInfoStore('assignedTo', response);
        setLoadInfoStore('assignedToButton', 'Assign Load');
        updateLoadPropertyAtIndex({
          coveredBy: response.name,
          coveredById: response.id,
          assignedToUser: response.name,
          assignedToUserId: response.id,
        });
      }
      if (!Boolean(load().assignedToGroup) && !Boolean(load().assignedToUser)) {
        setLoadInfoStore('assignedToButton', 'Assign Load');
      }
    })();
  });

  const editFlag = (index: number) => {
    setLoadInfoStore('flagIndex', index);
    openModal(editFlagModalId);
    setLoadInfoStore(
      'flag',
      (orderStore.order.loads[orderStore.activeTab.index].flags ?? [])[index],
    );
  };

  const handleFalveySuccessflow = async (
    commitQuoteResult: CommitQuoteResult,
  ) => {
    let vendorName = '';
    const falveyVendorIndex = (orderStore.order.vendors || []).findIndex(
      (v) => v.name === 'Falvey Shippers Insurance',
    );
    if (falveyVendorIndex !== -1 && orderStore.order.vendors) {
      vendorName = orderStore.order.vendors[falveyVendorIndex]
        .description as string;

      const vendorUpdateLineItems =
        size(orderStore.order.vendors) > 0
          ? [
              ...(orderStore.order.vendors[orderStore.activeTab.index]
                .lineItems as VendorLineItem[]),
            ]
          : [];
      const vendorLineItemIndex = vendorUpdateLineItems.findIndex(
        (lineItem) => lineItem.description === 'Certificate #',
      );

      vendorUpdateLineItems[vendorLineItemIndex] = {
        ...vendorUpdateLineItems[vendorLineItemIndex],
        description: `Certificate ${commitQuoteResult.certificateNumber}`,
        operationType: 'Update',
      };

      updateVendorPropertyAtIndex(
        {
          description: vendorName.concat(
            `\nPolicy Number: ${commitQuoteResult.policyNumber}`,
          ),
          lineItems: vendorUpdateLineItems,
        },
        falveyVendorIndex,
      );
      const res = await postOrderComment(
        {
          objectId: orderStore.order.id,
          type: 'Order',
          comment: `Certificate # ${commitQuoteResult.certificateNumber} and Policy Number: ${commitQuoteResult.policyNumber} has been generated for Falvey Shippers Insurance. \n Falvey COI Link: ${commitQuoteResult.certificatePDFLink}`,
        },
        CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsInsurance,
      );
      if (res?.isSuccess) {
        applyOrderHydration(() => {
          addCommentInOrderStore(res.value);
        });
      }
      //Note: Have to save order or else vendor data will delete
      await saveOrderDetails();
    }
  };

  const submitPickupRequest = async () => {
    try {
      if (pickupLoading() || orderStore.isSubmittingPickup) {
        return;
      }
      if (
        isFeatureFlagEnabled(FeatureFlags.PreventDispatchingForModifiedLoads)()
      ) {
        const stops = load().stops || [];
        if (stops.length > 2) {
          handleToast(
            ToastType.Error,
            'This load has more than two stops (one origin and one destination). Please dispatch this load manually.',
          );
          return;
        }
        if (stops.length === 2) {
          const [originStop, destinationStop] = [
            stops.find((stop) => stop.pickUp === true) || stops[0],
            stops.find((stop) => stop.pickUp === false) || stops[1],
          ];
          const loadItems = load().items || [];
          const loadData = {
            loadItems: loadItems as unknown as LTLLoadItem[],
            originCountry: originStop.countryCode ?? '',
            originState: originStop.state ?? '',
            originZip: originStop.zip,
            destinationCountry: destinationStop.countryCode ?? '',
            destinationState: destinationStop.state ?? '',
            destinationZip: destinationStop.zip,
          };
          const formattedQuoteParams = {
            loadItems: loadLTLItems(),
            originCountry: ltlQuoteParams().originCountry,
            originState: ltlQuoteParams().originState,
            originZip: ltlQuoteParams().originZip,
            destinationCountry: ltlQuoteParams().destinationCountry,
            destinationState: ltlQuoteParams().destinationState,
            destinationZip: ltlQuoteParams().destinationZip,
          };
          const validationResult = isRequoteNeeded(
            loadData,
            formattedQuoteParams,
            {
              compareBreakdowns: isFeatureFlagEnabled(
                FeatureFlags.LtlSupportMixedCommodityPalletOnLoad,
              )(),
            },
          );
          if (validationResult.isRequoteNeeded) {
            handleToast(
              ToastType.Error,
              getDetailedRequoteMessage(validationResult.differences),
            );
            return;
          }
        }
      }

      if (isOrderStoreDirty()) {
        handleToast(
          ToastType.Error,
          'Please save the order before submitting a pickup request.',
        );
        return;
      }
      const zipChanges = checkZipForChanges(
        load(),
        getEligibleEntries(load().stops),
      );
      if (zipChanges) {
        handleToast(
          ToastType.Error,
          'Zip code changes have been made to the stops on this load. Please re-quote the load.',
        );
        return;
      }
      setPickupLoading(true);
      setOrderStore('isSubmittingPickup', true);
      let resp;
      if (isFeatureFlagEnabled(FeatureFlags.TMSApiClientDispatch)()) {
        resp = await dispatchLtlLoad(load().id);
      } else {
        resp = await requestPickupLegacy(load());
      }
      setPickupLoading(false);
      setOrderStore('isSubmittingPickup', false);

      const { errors, certificatePDFLink } = resp.commitQuoteResult;

      if (certificatePDFLink !== null) {
        await handleFalveySuccessflow(resp.commitQuoteResult);
      } else if (errors && errors.length > 0) {
        if (errors[0] === 'Falvey Insurance already exists for this Order.') {
          handleToast(ToastType.Error, errors[0]);
        } else {
          handleToast(
            ToastType.Error,
            'There was an error sending the request for insurance to Falvey. The LTL Team has been notified and is resolving the issue.',
          );
          setOrderStore(
            'vendorFalveyInsuranceError',
            `There was an error sending your request for insurance to Falvey. The LTL Team has been notified of the issue and will manually update the insurance information below. Please reach out to ltl@armstrongtransport.com to confirm insurance has been added for ${
              orderStore.order.loads[orderStore.activeTab.index].loadNum
            }.`,
          );
          const falveyVendorIndex = (orderStore.order.vendors || []).findIndex(
            (v) => v.name === 'Falvey Shippers Insurance',
          );
          setActiveCarrierTab({
            index: falveyVendorIndex,
            type: 'vendor',
          } as TabState);
        }
      }
    } catch (error) {
      setPickupLoading(false);
      setOrderStore('isSubmittingPickup', false);
      handleToast(ToastType.Error, (error as Error).message);
    }
  };

  const hasRateConStatusForCurrentCarrier = () => {
    return (
      Boolean(load().secureRateConViewModels) &&
      load().secureRateConViewModels!.length > 0 &&
      load().carrierId != null &&
      load().secureRateConViewModels?.find(
        (c) => c.carrierId === load().carrierId,
      ) != null
    );
  };

  const getRateConStatusForLoad = () => {
    if (hasRateConStatusForCurrentCarrier()) {
      return load().secureRateConViewModels?.find(
        (c) => c.carrierId === load().carrierId,
      )?.status;
    }
  };

  const getNearbyCarriersDetails = async () => {
    const eligibleStops = getEligibleEntries<LastUsedTopStopModel>(
      load().stops,
    );
    if (
      eligibleStops.length > 0 &&
      eligibleStops[0].zip &&
      Boolean(eligibleStops[0].stopDateTime) &&
      DateTime.fromISO(eligibleStops[0].stopDateTime as string, {
        zone: 'utc',
      }).startOf('day') >= DateTime.now().startOf('day').minus({ days: 1 })
    ) {
      await fetchNearbyCarriers({
        zip: eligibleStops[0].zip.split('-')[0],
        startDate: DateTime.fromISO(eligibleStops[0].stopDateTime as string, {
          zone: 'utc',
        }).toISO() as string,
        equipment: load().equipment ?? '',
      });
    }
  };

  createEffect(async () => {
    if (!carrierStore.carrier[orderStore.activeTab.index]?.name) {
      await getNearbyCarriersDetails();
    }
  });

  onMount(async () => {
    if (
      orderStore.order.status == 'Unassigned' &&
      (orderStore.order.loads[orderStore.activeTab.index]?.stops?.length ??
        0) !== 0 &&
      orderStore.nearbyCarriers.length == 0
    ) {
      await getNearbyCarriersDetails();
    }
  });

  return (
    <CollapsibleCard
      class="mb-2"
      expanded={!orderStore.isMobile}
      headerContentClass="flex-wrap"
      startTitle={
        <Show
          when={!isCreate}
          fallback={<div class="text-lg">Load Information</div>}
        >
          <div class="text-lg">{`Load ${orderStore.order.id}-${
            orderStore.order.loads[orderStore.activeTab.index].sequence === 0
              ? orderStore.activeTab.index + 1
              : orderStore.order.loads[orderStore.activeTab.index].sequence
          }`}</div>
          <Show
            when={
              Boolean(
                orderStore.order.loads[orderStore.activeTab.index].billedDate,
              ) || orderStore.order.loads[orderStore.activeTab.index].paidDate
            }
          >
            <Box display="flex" gap={1}>
              <Show
                when={
                  orderStore.order.loads[orderStore.activeTab.index].billedDate
                }
              >
                <p class="text-sm font-medium leading-6 tracking-[.02em]">
                  Invoiced Date{' '}
                  {
                    orderStore.order.loads[orderStore.activeTab.index]
                      .billedDate
                  }
                </p>
              </Show>
              <Show
                when={
                  orderStore.order.loads[orderStore.activeTab.index].paidDate
                }
              >
                <p class="text-sm font-medium leading-6 tracking-[.02em]">
                  Paid Date{' '}
                  {orderStore.order.loads[orderStore.activeTab.index].paidDate}
                </p>
              </Show>
            </Box>
          </Show>
        </Show>
      }
      startTitleAction={
        <Show when={!orderStore.isMobile}>
          <LoadInformationActionButtons
            load={load()}
            tabIndex={orderStore.activeTab.index}
            currentFlagIndex={() => loadInfoStore.flagIndex}
            setCurrentFlagIndex={(idx) => setLoadInfoStore('flagIndex', idx)}
            setFlagValue={(flagType) => {
              setLoadInfoStore('flag', {
                description: '',
                flagType,
              });
            }}
            onAddFlag={() => openModal(editFlagModalId)}
          />
        </Show>
      }
    >
      <Stack class="overflow-x-hidden">
        <LoadSureWarning />
        <Show when={(load().flags ?? []).length > 0}>
          <div class="flex flex-col gap-y-2!">
            <Index each={load().flags}>
              {(item: Accessor<FlagViewModel>, index: number) => (
                // We can only have one "Needs Approval" per order.
                <Show when={item().flagType !== FlagType.NeedsApproval}>
                  <Notification
                    type={item().flagType}
                    text={
                      item().flagType === FlagType.AIEscalated ? (
                        <EscalatedFlagTextContent item={item} />
                      ) : (
                        <FlagNotificationTextContent item={item} />
                      )
                    }
                    subText={
                      item().flagType === FlagType.AIEscalated ? (
                        <EscalatedFlagSubTextContent item={item} />
                      ) : (
                        <FlagNotificationSubTextContent item={item} />
                      )
                    }
                    onClick={() => editFlag(index)}
                    commentLength={<FlagComments item={item} />}
                    cleared={
                      Boolean(item().cleared) &&
                      (softDeleteFlagTypes[
                        item().flagType as unknown as SpecialFlagTypeEnum
                      ] ||
                        false)
                    }
                    disputed={
                      (item().disputedOrderFlags ?? []).find((f) => {
                        return (
                          f.clearedDate === undefined || f.clearedDate === null
                        );
                      }) !== undefined
                    }
                    imgIcon={flagImgs[item().flagType]}
                  />
                </Show>
              )}
            </Index>
          </div>
        </Show>
        <div class="flex gap-2 flex-wrap mt-2!">
          <Box flex={1} maxWidth={'170px'} minWidth={'120px'}>
            <SelectInputFilled
              backgroundColor={getLoadColor(load().status)}
              label="Load Status"
              menuItems={getLoadStatusItemsForMode(
                load().mode,
                load().useStatusStateMachine,
                { hasCarrier: isCarrierSelected() },
              )}
              value={load().status}
              onChange={(value: string) => {
                markOrderLoadFieldChangeSource(
                  'status',
                  CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsStatus,
                );
                updateLoadPropertyAtIndex({
                  status: value,
                });
              }}
              disabled={orderStore.isReadOnly}
              dataTestId="load-status-select"
            />
          </Box>
          <CoveredBy load={load()} tabIndex={orderStore.activeTab.index} />
          <AssignedTo load={load()} tabIndex={orderStore.activeTab.index} />
          <Show when={isFeatureFlagEnabled(FeatureFlags.CapacityOpsRepUser)()}>
            <CapacityOpsRep
              load={load()}
              tabIndex={orderStore.activeTab.index}
            />
          </Show>
          <Stack justifyContent="center" direction="row" gap={1}>
            <Switch>
              <Match
                when={
                  load().mode === 'TL' ||
                  load().mode === 'Partial' ||
                  load().mode === 'Intermodal'
                }
              >
                <ShareAndUnShareCapacity
                  load={load()}
                  tabIndex={orderStore.activeTab.index}
                />
              </Match>
              <Match
                when={
                  load().mode == 'LTL' &&
                  load().status === 'Assigned' &&
                  load().id &&
                  ((load().ltlquoteId !== null &&
                    load().ltlquoteId !== undefined) ||
                    (load().quoteId !== null &&
                      load().quoteId !== undefined)) &&
                  userStore.user.permissions?.includes('Rate LTL') &&
                  !isLtlQuoteParamsLoading()
                }
              >
                <Button
                  size="sm"
                  startIcon={<TruckIcon />}
                  disabled={
                    pickupLoading() ||
                    orderStore.isSaving ||
                    orderStore.isSubmittingPickup
                  }
                  variant="contained"
                  onClick={submitPickupRequest}
                  color="primary"
                  dataTestId={formatDataTestId(
                    'Submit Pickup Request',
                    'button',
                  )}
                  class="!h-[36px]"
                >
                  <Show when={pickupLoading()}>
                    <span class="loading loading-spinner loading-sm" />
                  </Show>
                  Submit Pickup Request
                </Button>
              </Match>
            </Switch>
            <Show
              when={
                (userStore.user.permissions?.includes('Rate LTL') ?? false) &&
                load().mode === 'LTL' &&
                (load().status === 'Unassigned' ||
                  load().status === 'Assigned') &&
                !Boolean(query.copyOrderId) &&
                !isLtlQuoteParamsLoading()
              }
            >
              <GetRates
                load={load()}
                ltlQuoteParams={ltlQuoteParams()}
                loadLTLItems={loadLTLItems()}
                isLoading={isLtlQuoteParamsLoading()}
              />
            </Show>
            <Show
              when={
                isCapacityFinderEnabled() &&
                load().mode !== 'LTL' &&
                !!load().id
              }
            >
              <Box>
                <Button
                  variant="contained"
                  size="sm"
                  class="text-armstrong-sm!"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => {
                    window.open(
                      `/capacity/finder?loadNum=${load().loadNum}`,
                      '_blank',
                    );
                  }}
                  dataTestId="find-capacity"
                >
                  Find Capacity
                </Button>
              </Box>
            </Show>
            <LaneHistory load={load()} enabled={isLaneHistoryEnabled()} />
          </Stack>
        </div>
        <LTLInfo
          useNewTables={isFeatureFlagEnabled(
            FeatureFlags.ltl_VNEXT_2856_TableRefactor_ReadFromNew,
          )()}
        />
        <div class="mt-4">
          <div class="block 1080p:!hidden">
            <LoadDetailsRow
              load={load()}
              tabIndex={orderStore.activeTab.index}
            />
          </div>
          <Grid container spacing={4}>
            <Show
              when={!carrierStore.loading}
              fallback={
                <Grid
                  item
                  size={{
                    xs: orderStore.is1080p ? 6 : 12,
                    md: orderStore.is1080p ? 6 : 8,
                  }}
                >
                  <CardPlaceholder />
                </Grid>
              }
            >
              <Grid
                item
                size={{
                  xs: orderStore.is1080p ? 6 : 12,
                  md: orderStore.is1080p ? 6 : 8,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <div class="text-base font-normal text-[#123B50] tracking-[0.00938em]">
                    Carrier Details
                  </div>
                  <ProfileFallout
                    load={load()}
                    tabIndex={orderStore.activeTab.index}
                    disabled={orderStore.isReadOnly}
                  />
                </Stack>
                <Stack
                  flex={3}
                  gap={2}
                  class="border border-solid rounded-lg border-gray-500 p-2"
                  backgroundColor="#FFFBF6"
                  sx={{
                    'box-shadow':
                      '0px 1px 10px 0px rgba(0, 0, 0, 0.12), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 2px 4px -1px rgba(0, 0, 0, 0.20)',
                  }}
                >
                  <div class="flex flex-col gap-y-2">
                    <SystemAlerts />
                    {carrierStore.carrier[orderStore.activeTab.index]?.name ||
                    orderStore.nearbyCarriers.length <= 0 ? null : (
                      <Notification
                        type="warning"
                        onClick={() => openModal(nearByCarriersModalId)}
                        text={`Armstrong has ${orderStore.nearbyCarriers.length} carriers dropping off loads near your first pickup.`}
                        cleared={false}
                      />
                    )}
                  </div>
                  <CarrierDetailActionButtons
                    load={load()}
                    tabIndex={orderStore.activeTab.index}
                    isCarrierSelected={isCarrierSelected()}
                    isCapacityFinderEnabled={isCapacityFinderEnabled()}
                  />
                  <div class="flex gap-x-2">
                    <div class="w-full">
                      <CarrierAutoComplete
                        load={load()}
                        tabIndex={orderStore.activeTab.index}
                        disabled={
                          orderStore.isReadOnly ||
                          (isFeatureFlagEnabled(
                            FeatureFlags.feature_order_block_save_on_neg_credit_limit,
                          )() &&
                            isCreditLimitExceeded() &&
                            !orderContainsLTLLoad() &&
                            (orderStore.isCreate ||
                              (hasQualifiedStatus(
                                orderStore.order.loads[
                                  orderStore.activeTab.index
                                ].status,
                              ) &&
                                !carrierStore.carrier[
                                  orderStore.activeTab.index
                                ]?.name)))
                        }
                        size="medium"
                      />
                    </div>
                    <Stack direction="column-reverse">
                      <Show
                        when={carrierProfileId()}
                        fallback={
                          <Button
                            sx={{ 'max-width': '105px' }}
                            variant="contained"
                            class="text-armstrong-sm!"
                            startIcon={<OpenInNewIcon />}
                            dataTestId="carrier-profile"
                            disabled
                          >
                            Profile
                          </Button>
                        }
                      >
                        {(carrierId) => (
                          <A
                            href={carrierUrls.profile(carrierId())}
                            target="_blank"
                          >
                            <Button
                              sx={{ 'max-width': '105px' }}
                              variant="contained"
                              class="text-armstrong-sm!"
                              startIcon={<OpenInNewIcon />}
                              dataTestId="carrier-profile"
                            >
                              Profile
                            </Button>
                          </A>
                        )}
                      </Show>
                    </Stack>
                  </div>
                  <Box display="flex" gap={1} alignItems="center">
                    <Show
                      when={
                        isFeatureFlagEnabled(
                          FeatureFlags.UseHighwaySecureRateCon,
                        )() &&
                        Boolean(load().secureRateConViewModels) &&
                        load().secureRateConViewModels!.length > 0
                      }
                    >
                      <Tooltip
                        title="View previously sent Rate Confirmations and statuses"
                        class="z-50! shadow-lg! text-left! bg-white! text-black! after:bg-white!"
                        placement="right"
                        dataTestId="secure-rate-confirmation-tooltip"
                      >
                        <Box
                          class="bg-[#026EA1] rounded-full flex items-center cursor-pointer px-[8px] py-1 text-white "
                          onClick={() => {
                            openModal(
                              `SecureRateConfirmation-${orderStore.activeTab.index}`,
                            );
                          }}
                        >
                          <img
                            src={HighwayIcon}
                            alt="Highway Icon"
                            class="size-7"
                          />
                          <Typography
                            variant="body1"
                            component="span"
                            class="inline-block font-roboto text-sm font-medium uppercase pr-2"
                          >
                            Rate Con Status
                          </Typography>
                          <Show when={hasRateConStatusForCurrentCarrier()}>
                            <Typography
                              variant="body1"
                              component="span"
                              class={`${
                                rateConStatusBgAndTextColor[
                                  load().secureRateConViewModels?.[0]
                                    ?.status as keyof typeof rateConStatusBgAndTextColor
                                ]
                              } px-[8px] inline-block rounded-full capitalize font-semibold text-xs leading-[1.4]`}
                            >
                              {getRateConStatusForLoad()}
                            </Typography>
                          </Show>
                        </Box>
                      </Tooltip>
                    </Show>
                    <Box class="flex gap-x-2 items-center border-gray-500 border w-fit px-2 rounded-md bg-white">
                      <Checkbox
                        label="Signed Rate Confirmation"
                        checked={load().signedRateCon}
                        onChange={(checked) => {
                          updateLoadPropertyAtIndex({
                            signedRateCon: checked,
                            signedConfirmationDate:
                              load().signedConfirmationDate ??
                              DateTime.now().toISO(),
                          });
                        }}
                        disabled={!isCarrierSelected() || orderStore.isReadOnly}
                        class="text-xs! ml-[-9px]"
                        size="xs"
                        color="info"
                        dataTestId={'signed-rate-confirmation-checkboxInput'}
                      />
                      {load().signedRateCon && (
                        <div class="text-xs mb-1 font-medium">
                          {`- ${DateTime.fromISO(
                            load().signedConfirmationDate?.toString() ??
                              DateTime.now().toISO(),
                            { zone: 'utc' },
                          )
                            .setZone('local')
                            .toFormat('MMM dd, yyyy hh:mm a')}`}
                        </div>
                      )}
                    </Box>
                  </Box>

                  <CarrierContact
                    load={load()}
                    tabIndex={orderStore.activeTab.index}
                    isCarrierSelected={isCarrierSelected()}
                  />
                </Stack>
              </Grid>
            </Show>
            <Grid
              item
              size={{
                xs: orderStore.is1080p ? 6 : 12,
                md: orderStore.is1080p ? 6 : 4,
              }}
            >
              <Grid container spacing={4}>
                <Grid item size={orderStore.is1080p ? 6 : 12}>
                  <DriverDetails
                    load={load()}
                    tabIndex={orderStore.activeTab.index}
                    trackingData={trackingData()}
                  />
                </Grid>
                <Show when={orderStore.is1080p}>
                  <Grid item size={6}>
                    <LoadDetails
                      load={load()}
                      tabIndex={orderStore.activeTab.index}
                    />
                  </Grid>
                </Show>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Stack>
      {/* NOTE: Restricted carrier modals are currently commented out. Please don't remove the code. */}
      {/* <AcknowledgeCarrierRestrictionModal
        matchedOn="CarrierId"
        message="This carrier has an unresolved claim with this customer."
        reason="CLAIM"
        onAcknowledge={() => {
          // proceed with assigning the carrier
        }}
      />
      <OverrideRestrictedCarrierModal
        matchedOn="MC 123456"
        message="This customer has requested that this carrier not be used."
        reason="CUSTOMER_REQUEST"
        onOverride={ () => {
          // call the override/audit API with `justification`, then proceed with assigning the carrier
          closeModal(OVERRIDE_RESTRICTED_CARRIER_MODAL_ID);
        }}
        onChooseDifferentCarrier={() => {
         closeModal(OVERRIDE_RESTRICTED_CARRIER_MODAL_ID);
        }}
      /> */}
      <FlagPopup
        modalId={editFlagModalId}
        flag={loadInfoStore.flag}
        mode={load().mode ?? ''}
        changeTrackingSources={{
          addComment:
            CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsFlagAddComment,
          deleteComment:
            CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsFlagDeleteComment,
          dispute: CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsFlagDispute,
        }}
        onClose={() => {
          setLoadInfoStore('flagIndex', null);
          closeModal(editFlagModalId);
          setLoadInfoStore('flag', undefined);
        }}
        onChange={(flag, action) =>
          onFlagChange(
            flag,
            action,
            load(),
            orderStore.activeTab.index,
            editFlagModalId,
          )
        }
        onDispute={(needsApprovalFlag) => {
          const loads = [...unwrap(orderStore.order.loads)];

          if (
            typeof loadInfoStore.flagIndex === 'number' &&
            loads[orderStore.activeTab.index].flags !== undefined
          ) {
            loads[orderStore.activeTab.index] = {
              ...loads[orderStore.activeTab.index],
              flags: [...(loads[orderStore.activeTab.index].flags ?? [])],
            };
            // @ts-expect-error flags can't be undefined here.
            loads[orderStore.activeTab.index].flags[loadInfoStore.flagIndex] = {
              // @ts-expect-error flags can't be undefined here.
              ...loads[orderStore.activeTab.index].flags[
                loadInfoStore.flagIndex
              ],
              disputedOrderFlags: needsApprovalFlag.disputedOrderFlags,
            };
          }

          updateOrderState({
            needsApprovalFlag,
            loads,
          });
        }}
        loading={loadInfoStore.isLoading}
        isLTL={load().mode === 'LTL'}
      />
      <BasicModal
        id={`${ASSIGN_LOAD_ID}-${orderStore.activeTab.index}`}
        title="Assign load"
        width={'1000px'}
        showClose={false}
        onClose={() =>
          closeModal(`${ASSIGN_LOAD_ID}-${orderStore.activeTab.index}`)
        }
        footer={false}
      >
        <AssignLoad
          onClose={() =>
            closeModal(`${ASSIGN_LOAD_ID}-${orderStore.activeTab.index}`)
          }
          load={load()}
          tabIndex={orderStore.activeTab.index}
        />
      </BasicModal>
      <BasicModal
        id={addPlaylistModalId}
        title=""
        width={'800px'}
        onClose={() => closeModal(addPlaylistModalId)}
        footer={false}
        header={false}
      >
        <SelectPlaylist
          carrierId={carrierStore.carrier[orderStore.activeTab.index]?.id}
          tabIndex={orderStore.activeTab.index}
          onClose={() => closeModal(addPlaylistModalId)}
          modalId={addPlaylistModalId}
          load={load()}
          changeTrackingSource={
            CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsPlaylist
          }
        />
      </BasicModal>
      <BasicModal
        id={nearByCarriersModalId}
        title="Nearby Carriers"
        footer={false}
        width="90VW"
        showClose={true}
      >
        <NearbyCarrier />
      </BasicModal>

      <BasicModal
        id={`SecureRateConfirmation-${orderStore.activeTab.index}`}
        title="Rate Confirmation Activity History"
        footer={false}
        width="800px"
        maxWidth="90vw"
        showClose={true}
      >
        <RateConfirmationActivityHistory
          carrierName={load().carrierName}
          secureRateConViewModels={load().secureRateConViewModels}
        />
      </BasicModal>
    </CollapsibleCard>
  );
};
